/**
 * @file DeliverySourceNote.tsx
 * @description Строка происхождения условий доставки в блоке «Доставка»
 * (phase19.24 шаг 5.2, §3).
 *
 * Отвечает на вопрос «кто это выставил и когда»: выбор клиента на витрине и
 * условия менеджера выглядят в карточке одинаково, а менять их можно
 * по-разному — витринный выбор правим только через повторное согласование.
 * Без подписи менеджер спорил с клиентом о том, кто назвал цену.
 */

import { Globe, UserRound } from "lucide-react";

import type { OrderDeliveryReceiptDto } from "../../../lib/api-delivery-agreement";
import { formatShopDateTime } from "../../../lib/shop-time";

interface Props {
  /** Снапшот условий; `null` — способ ещё не выставлен. */
  receipt: OrderDeliveryReceiptDto | null;
  /** Заказ завели на витрине — это видно и без снапшота. */
  siteOrder: boolean;
}

export function DeliverySourceNote({ receipt, siteOrder }: Props) {
  if (!receipt) {
    return (
      <p className="text-sm text-on-surface-variant">
        {siteOrder
          ? "Заказ с сайта: клиент выбрал «доставку по договорённости» — способ и цену выставляет менеджер."
          : "Условия доставки ещё не выставлены."}
      </p>
    );
  }

  const fromSite = receipt.source === "SITE";
  const Icon = fromSite ? Globe : UserRound;
  return (
    <p className="flex flex-wrap items-center gap-1.5 text-sm text-on-surface-variant">
      <Icon className="size-4 shrink-0" aria-hidden />
      <span className="min-w-0">
        {fromSite ? "Выбрано клиентом на сайте" : "Условия выставил менеджер"}
        {/* Даты может не быть в старом снапшоте — тогда просто не печатаем её,
            а не показываем «—» рядом с «выбрано клиентом». */}
        {receipt.setAt ? ` · ${formatShopDateTime(receipt.setAt)}` : ""}
        {receipt.quote?.quotedAt
          ? ` · расчёт ${formatShopDateTime(receipt.quote.quotedAt)}`
          : ""}
      </span>
    </p>
  );
}
